import { _ } from "../../polyglot/polyglot.js";
import { select, stacker } from "../../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ConsecutiveMonths from "../../../models/period/ConsecutiveMonths.js";

/**
 * `ConsecutiveMonthsSelectionWidget` contains two dropdowns to choose the start
 * and end of a ConsecutiveMonths period.
 *
 * @param {FiscalYear} fiscalYear - The fiscal year the selection will
 *   be referring to.
 * @param {ConsecutiveMonths} [consecutiveMonths] - The initial
 *   selection for the widget.  It must be within the fiscal
 *   year. Defaults to the last two months of fiscal year.
 * @param {function(ConsecutiveMonths): void} [onChange] - Called with the
 *   current ConsecutiveMonths selected as argument each time there is
 *   a change
 */
export default class ConsecutiveMonthsSelectionWidget extends Widget {
	constructor({
		fiscalYear,
		onChange = (_consecutiveMonths) => {},
		consecutiveMonths,
	}) {
		super(...arguments);

		this._fiscalYear = fiscalYear;
		this._onChange = onChange;

		const allMonths = fiscalYear.asMonthsWithinActivePeriod();
		this._startMonths = allMonths.slice(0, allMonths.length - 1);
		this._endMonths = allMonths.slice(1, allMonths.length);

		const initialSelection =
			consecutiveMonths ||
			fiscalYear.asLastConsecutiveMonthsWithinActivePeriod({ length: 2 });

		this._startIndex = initialSelection.startMonth().getPeriodIndex();
		this._endIndex = initialSelection.endMonth().getPeriodIndex();
	}

	renderContentOn(html) {
		html.div(
			{ class: "ConsecutiveMonthsSelectionWidget" },
			stacker(
				{ direction: "horizontal" },
				this._renderStartDropdownOn(html),
				this._renderEndDropdownOn(html),
			),
		);
	}

	_renderStartDropdownOn(html) {
		return stacker(
			{ direction: "horizontal" },
			html.label(_("From")),
			this._makeStartSelection(),
		);
	}

	_renderEndDropdownOn(html) {
		return stacker(
			{ direction: "horizontal" },
			html.label(_("To")),
			this._makeEndSelection(),
		);
	}

	_getSelectedPeriod() {
		return new ConsecutiveMonths({
			year: this._fiscalYear,
			periodIndex: this._startIndex,
			additionalMonths: this._endIndex - this._startIndex,
		});
	}

	_makeEndSelection() {
		const months = this._endMonths.filter(
			(month) => month.getPeriodIndex() > this._startIndex,
		);

		return select({
			name: "to",
			class: "month-selection",
			items: this._makeMonthItems({ months }),
			selected: this._endIndex,
			onChange: (periodIndex) => {
				this._endIndex = periodIndex;
				this._onSelectionChanged();
			},
		});
	}

	_makeStartSelection() {
		const months = this._startMonths.filter(
			(month) => month.getPeriodIndex() < this._endIndex,
		);

		return select({
			name: "from",
			class: "month-selection",
			items: this._makeMonthItems({ months }),
			selected: this._startIndex,
			onChange: (periodIndex) => {
				this._startIndex = periodIndex;
				this._onSelectionChanged();
			},
		});
	}

	_makeMonthItems({ months }) {
		return months.map((month) => ({
			value: month.getPeriodIndex(),
			label: month.getName(),
		}));
	}

	_onSelectionChanged() {
		this.update();
		this._onChange(this._getSelectedPeriod());
	}
}
